import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { ClipboardCheck, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

function fmt(iso) {
  // Phase B2 — `fmt` is used outside React render context in this file, so we
  // can't call `useLocale()` directly. Locked to en-GB (the platform default
  // for the UK SaaS target). Inside JSX renders, use useLocale().formatDate.
  if (!iso) return "—";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}

export default function PermitsToWork() {
  const [permits, setPermits] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    contractor_company_id: "", site_id: "", permit_type: "Hot Work",
    valid_from: "", valid_to: "", scope_description: "",
  });

  const refresh = async () => {
    try {
      const [p, cc, s] = await Promise.all([
        api.get("/permits-to-work"),
        api.get("/contractor-companies"),
        api.get("/sites"),
      ]);
      setPermits(p.data?.items ?? p.data ?? []);
      setCompanies(cc.data?.items ?? cc.data ?? []);
      setSites(s.data ?? []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); }, []);

  const onCreate = async () => {
    try {
      await api.post("/permits-to-work", {
        ...form,
        valid_from: new Date(form.valid_from).toISOString(),
        valid_to: new Date(form.valid_to).toISOString(),
      });
      toast.success("Permit created");
      setOpen(false);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="permits-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-navy-900 tracking-tight flex items-center gap-2">
              <ClipboardCheck className="w-7 h-7 text-accent" /> Permits to work
            </h1>
            <p className="text-sm text-navy-500 mt-1">Hot Work · Confined Space · Working at Height · Other</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-accent hover:bg-accent/90 text-white" data-testid="new-permit-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New permit
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New permit-to-work</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-navy-600">Contractor company</label>
                  <select className="w-full mt-1 px-3 py-2 border border-navy-200 rounded-lg" value={form.contractor_company_id}
                    onChange={(e) => setForm({ ...form, contractor_company_id: e.target.value })} data-testid="permit-cc-select">
                    <option value="">— select —</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-navy-600">Site</label>
                  <select className="w-full mt-1 px-3 py-2 border border-navy-200 rounded-lg" value={form.site_id}
                    onChange={(e) => setForm({ ...form, site_id: e.target.value })} data-testid="permit-site-select">
                    <option value="">— select —</option>
                    {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-navy-600">Type</label>
                  <select className="w-full mt-1 px-3 py-2 border border-navy-200 rounded-lg" value={form.permit_type}
                    onChange={(e) => setForm({ ...form, permit_type: e.target.value })} data-testid="permit-type-select">
                    <option>Hot Work</option><option>Confined Space</option>
                    <option>Working at Height</option><option>Other</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-navy-600">Valid from</label>
                    <input type="date" className="w-full mt-1 px-3 py-2 border border-navy-200 rounded-lg"
                      value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-navy-600">Valid to</label>
                    <input type="date" className="w-full mt-1 px-3 py-2 border border-navy-200 rounded-lg"
                      value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-navy-600">Scope description</label>
                  <textarea rows={3} className="w-full mt-1 px-3 py-2 border border-navy-200 rounded-lg"
                    value={form.scope_description} onChange={(e) => setForm({ ...form, scope_description: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={onCreate} className="bg-accent hover:bg-accent/90 text-white" data-testid="permit-submit">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {permits.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-navy-200 p-10 text-center text-navy-500">
            No permits yet.
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-navy-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-navy-50 text-navy-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Site</th>
                  <th className="px-4 py-3 text-left">Valid from</th>
                  <th className="px-4 py-3 text-left">Valid to</th>
                  <th className="px-4 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100">
                {permits.map((p) => {
                  const site = sites.find(s => s.id === p.site_id);
                  return (
                    <tr key={p.id} data-testid={`permit-row-${p.id}`}>
                      <td className="px-4 py-3 font-medium text-navy-900">{p.permit_type}</td>
                      <td className="px-4 py-3 text-navy-600">{site?.name || p.site_id}</td>
                      <td className="px-4 py-3 text-navy-600">{fmt(p.valid_from)}</td>
                      <td className="px-4 py-3 text-navy-600">{fmt(p.valid_to)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                          p.status === "active" ? "bg-success-50 text-success-700" :
                          p.status === "scheduled" ? "bg-navy-50 text-navy-600" :
                          "bg-danger-50 text-danger-700"
                        }`}>{p.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
